import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Pencil, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { practiceComplianceAPI as API, RISK_BAND_META, riskBand } from '../../lib/practiceComplianceAPI';

const LC = [1, 2, 3, 4, 5];
const CATEGORIES = ['Professional', 'Regulatory', 'Cyber', 'Financial', 'Operational'];
const EMPTY = { category: 'Operational', title: '', description: '', inherent_likelihood: 3, inherent_consequence: 3, controls: '', residual_likelihood: 3, residual_consequence: 3, owner: '', treatment: '', due_date: '', status: 'open' };

const ScoreBadge = ({ l, c }) => {
  const score = (l || 1) * (c || 1);
  const band = riskBand(score);
  return <Badge className={(RISK_BAND_META[band] || {}).color}>{score} {(RISK_BAND_META[band] || {}).label}</Badge>;
};

export const PMRiskRegisterTab = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // risk object or 'new'
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const res = await API.listRisks(); setData(res.data); } catch { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (risk) => {
    setF(risk ? { ...EMPTY, ...risk } : EMPTY);
    setEditing(risk || 'new');
  };

  const save = async () => {
    setBusy(true);
    const payload = {
      category: f.category, title: f.title, description: f.description,
      inherent_likelihood: Number(f.inherent_likelihood), inherent_consequence: Number(f.inherent_consequence),
      controls: f.controls, residual_likelihood: Number(f.residual_likelihood), residual_consequence: Number(f.residual_consequence),
      owner: f.owner, treatment: f.treatment, due_date: f.due_date, status: f.status,
    };
    try {
      if (editing === 'new') await API.createRisk(payload);
      else await API.updateRisk(editing.id, payload);
      toast.success('Risk saved — residual score recalculated');
      setEditing(null);
      await load();
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save risk'); }
    finally { setBusy(false); }
  };

  const review = async (risk) => {
    try { await API.reviewRisk(risk.id); toast.success(`${risk.risk_id} marked reviewed`); await load(); }
    catch { toast.error('Failed'); }
  };

  if (!data) return <div className="animate-pulse h-48 bg-muted rounded-lg mt-4" />;

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {Object.entries(data.bands || {}).map(([band, n]) => (
            <Badge key={band} className={(RISK_BAND_META[band] || {}).color}>{(RISK_BAND_META[band] || {}).label}: {n}</Badge>
          ))}
        </div>
        {data.can_manage && (
          <Button size="sm" onClick={() => open(null)} data-testid="pm-add-risk"><Plus className="w-4 h-4 mr-1" /> Add risk</Button>
        )}
      </div>

      <Card>
        <CardContent className="pt-4 overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-muted/50">
              <TableHead>ID</TableHead><TableHead>Risk</TableHead><TableHead>Category</TableHead>
              <TableHead>Inherent</TableHead><TableHead>Residual</TableHead><TableHead>Status</TableHead>
              <TableHead>Last review</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.risks.map((r) => (
                <TableRow key={r.id} data-testid={`pm-risk-${r.risk_id}`}>
                  <TableCell className="font-mono text-xs">{r.risk_id}</TableCell>
                  <TableCell>
                    <p className="text-sm font-medium">{r.title}</p>
                    {r.training_requirement_ids?.length > 0 && <a href="/compliance/cpd?tab=training" className="text-xs text-[#0055FF] underline" data-testid={`pm-risk-training-${r.risk_id}`}>Training required ({r.training_requirement_ids.length})</a>}
                    <p className="text-xs text-muted-foreground max-w-xs truncate">{r.controls}</p>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.category}</TableCell>
                  <TableCell><ScoreBadge l={r.inherent_likelihood} c={r.inherent_consequence} /></TableCell>
                  <TableCell><ScoreBadge l={r.residual_likelihood} c={r.residual_consequence} /></TableCell>
                  <TableCell className="text-xs capitalize">{r.status}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.last_reviewed_at ? new Date(r.last_reviewed_at).toLocaleDateString('en-AU') : 'Not reviewed'}</TableCell>
                  <TableCell>
                    {data.can_manage && (
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => open(r)} data-testid={`pm-edit-risk-${r.risk_id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => review(r)} title="Mark reviewed"><CheckCircle2 className="w-4 h-4 text-green-600" /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-xs text-muted-foreground mt-3">
            Seeded scores are illustrative only and require firm assessment — likelihood × consequence (1–5). 1–4 Low · 5–9 Moderate · 10–16 High · 17–25 Critical. High/Critical require a documented treatment plan.
          </p>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing === 'new' ? 'Add risk' : `Edit ${editing?.risk_id} — ${editing?.title}`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Title *</Label><Input value={f.title} onChange={(e) => setF((x) => ({ ...x, title: e.target.value }))} data-testid="pm-risk-title" /></div>
              <div className="space-y-1"><Label className="text-xs">Category</Label>
                <Select value={f.category} onValueChange={(v) => setF((x) => ({ ...x, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Description (event / cause / impact)</Label><Textarea rows={2} value={f.description || ''} onChange={(e) => setF((x) => ({ ...x, description: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="border rounded-lg p-3 space-y-2">
                <div className="text-xs font-medium flex items-center justify-between">Inherent (before controls) <ScoreBadge l={f.inherent_likelihood} c={f.inherent_consequence} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Likelihood</Label>
                    <Select value={String(f.inherent_likelihood)} onValueChange={(v) => setF((x) => ({ ...x, inherent_likelihood: v }))}>
                      <SelectTrigger data-testid="pm-risk-il"><SelectValue /></SelectTrigger>
                      <SelectContent>{LC.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Consequence</Label>
                    <Select value={String(f.inherent_consequence)} onValueChange={(v) => setF((x) => ({ ...x, inherent_consequence: v }))}>
                      <SelectTrigger data-testid="pm-risk-ic"><SelectValue /></SelectTrigger>
                      <SelectContent>{LC.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="border rounded-lg p-3 space-y-2">
                <div className="text-xs font-medium flex items-center justify-between">Residual (after controls) <ScoreBadge l={f.residual_likelihood} c={f.residual_consequence} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Likelihood</Label>
                    <Select value={String(f.residual_likelihood)} onValueChange={(v) => setF((x) => ({ ...x, residual_likelihood: v }))}>
                      <SelectTrigger data-testid="pm-risk-rl"><SelectValue /></SelectTrigger>
                      <SelectContent>{LC.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Consequence</Label>
                    <Select value={String(f.residual_consequence)} onValueChange={(v) => setF((x) => ({ ...x, residual_consequence: v }))}>
                      <SelectTrigger data-testid="pm-risk-rc"><SelectValue /></SelectTrigger>
                      <SelectContent>{LC.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Controls</Label><Textarea rows={2} value={f.controls || ''} onChange={(e) => setF((x) => ({ ...x, controls: e.target.value }))} data-testid="pm-risk-controls" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs">Owner</Label><Input value={f.owner || ''} onChange={(e) => setF((x) => ({ ...x, owner: e.target.value }))} /></div>
              <div className="space-y-1"><Label className="text-xs">Treatment due</Label><Input type="date" value={f.due_date || ''} onChange={(e) => setF((x) => ({ ...x, due_date: e.target.value }))} /></div>
              <div className="space-y-1"><Label className="text-xs">Status</Label>
                <Select value={f.status || 'open'} onValueChange={(v) => setF((x) => ({ ...x, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['open', 'monitoring', 'treated', 'closed'].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Treatment plan</Label><Textarea rows={2} value={f.treatment || ''} onChange={(e) => setF((x) => ({ ...x, treatment: e.target.value }))} /></div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} disabled={busy || !f.title} data-testid="pm-risk-save">Save risk</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
